export type StreamStatus = 'online' | 'offline' | 'error';
export type AlertType = 'success' | 'no-response' | 'invalid-url' | 'no-audio';

export interface StreamVerificationResult {
  status: StreamStatus;
  alert: AlertType;
  streamUrl: string;
  lastVerification: string;
  responseTime: number;
  detectedFormat: string;
  detectedBitrate: string;
  streamName: string;
  listeners: number;
  peakListeners: number;
  genre: string;
  contentType: string;
  serverType: string;
  audioCodec: string;
  sampleRate: string;
  channels: string;
  metadataSupported: boolean;
  icecastUrl: string;
}

export interface AlertMessage {
  type: AlertType;
  title: string;
  description: string;
  icon: string;
  colorClasses: string;
  bgClasses: string;
}

export const alertMessages: AlertMessage[] = [
  {
    type: 'success',
    title: 'Stream funcionando',
    description: 'La transmisión está activa y respondiendo correctamente. El audio se detecta sin problemas y el formato es compatible con RadiofrecuenciaWeb.',
    icon: 'ri-check-double-line',
    colorClasses: 'text-emerald-600',
    bgClasses: 'bg-emerald-50 border-emerald-200',
  },
  {
    type: 'no-response',
    title: 'Stream no responde',
    description: 'El servidor de stream no está respondiendo. Verificá que la URL sea correcta, que el servidor esté encendido y que el puerto esté abierto en el firewall.',
    icon: 'ri-timer-line',
    colorClasses: 'text-amber-600',
    bgClasses: 'bg-amber-50 border-amber-200',
  },
  {
    type: 'invalid-url',
    title: 'URL inválida',
    description: 'La URL ingresada no tiene un formato válido. Asegurate de incluir el protocolo (http:// o https://) y que la dirección apunte directamente al stream, no a una página web.',
    icon: 'ri-error-warning-line',
    colorClasses: 'text-red-600',
    bgClasses: 'bg-red-50 border-red-200',
  },
  {
    type: 'no-audio',
    title: 'Audio no detectado',
    description: 'El servidor responde pero no se detecta una señal de audio. Puede deberse a que la fuente de audio no está conectada, el encoder está detenido o el nivel de volumen es demasiado bajo.',
    icon: 'ri-volume-mute-line',
    colorClasses: 'text-amber-600',
    bgClasses: 'bg-amber-50 border-amber-200',
  },
];

export function simulateVerification(streamUrl: string): Promise<StreamVerificationResult> {
  return new Promise((resolve, reject) => {
    const delay = 1800 + Math.random() * 1200;

    setTimeout(() => {
      if (!streamUrl || !streamUrl.match(/^https?:\/\/.+/)) {
        resolve({
          status: 'error',
          alert: 'invalid-url',
          streamUrl: streamUrl || '',
          lastVerification: formatDateTime(new Date()),
          responseTime: 0,
          detectedFormat: '-',
          detectedBitrate: '-',
          streamName: '-',
          listeners: 0,
          peakListeners: 0,
          genre: '-',
          contentType: '-',
          serverType: '-',
          audioCodec: '-',
          sampleRate: '-',
          channels: '-',
          metadataSupported: false,
          icecastUrl: '-',
        });
        return;
      }

      const isOnline = Math.random() > 0.2;

      if (isOnline) {
        const formats = ['MP3', 'AAC', 'AAC+', 'OGG'];
        const format = formats[Math.floor(Math.random() * formats.length)];
        const bitrates = ['64 kbps', '96 kbps', '128 kbps', '192 kbps', '256 kbps', '320 kbps'];
        const bitrate = bitrates[Math.floor(Math.random() * bitrates.length)];
        const genres = ['Pop Latino', 'Rock en Español', 'Música Latina', 'Electrónica', 'Noticias', 'Deportes'];
        const serverTypes = ['Icecast 2.4.4', 'SHOUTcast 2.6.1', 'Icecast 2.4.6', 'AudioRelay v3'];

        resolve({
          status: 'online',
          alert: 'success',
          streamUrl,
          lastVerification: formatDateTime(new Date()),
          responseTime: Math.floor(Math.random() * 400) + 30,
          detectedFormat: format,
          detectedBitrate: bitrate,
          streamName: 'Radio Nueva Actitud - Live',
          listeners: Math.floor(Math.random() * 350) + 1,
          peakListeners: Math.floor(Math.random() * 600) + 50,
          genre: genres[Math.floor(Math.random() * genres.length)],
          contentType: `audio/${format === 'AAC+' ? 'aacp' : format.toLowerCase()}`,
          serverType: serverTypes[Math.floor(Math.random() * serverTypes.length)],
          audioCodec: format === 'MP3' ? 'MPEG 1 Layer III' : format === 'AAC' ? 'AAC LC' : format === 'OGG' ? 'Vorbis' : 'HE-AAC v2',
          sampleRate: '44100 Hz',
          channels: 'Estéreo (2)',
          metadataSupported: true,
          icecastUrl: streamUrl.replace('/live', '/status-json.xsl'),
        });
      } else {
        const isNoResponse = Math.random() > 0.4;

        resolve({
          status: isNoResponse ? 'offline' : 'error',
          alert: isNoResponse ? 'no-response' : 'no-audio',
          streamUrl,
          lastVerification: formatDateTime(new Date()),
          responseTime: isNoResponse ? Math.floor(Math.random() * 8000) + 2000 : Math.floor(Math.random() * 200) + 40,
          detectedFormat: isNoResponse ? '-' : 'MP3',
          detectedBitrate: isNoResponse ? '-' : '128 kbps',
          streamName: '-',
          listeners: 0,
          peakListeners: 0,
          genre: '-',
          contentType: '-',
          serverType: isNoResponse ? 'No disponible' : 'Icecast 2.4.4',
          audioCodec: isNoResponse ? '-' : 'MPEG 1 Layer III',
          sampleRate: isNoResponse ? '-' : '44100 Hz',
          channels: isNoResponse ? '-' : 'Estéreo (2)',
          metadataSupported: false,
          icecastUrl: '-',
        });
      }
    }, delay);
  });
}

function formatDateTime(date: Date): string {
  const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  const d = date.getDate();
  const m = meses[date.getMonth()];
  const y = date.getFullYear();
  const h = date.getHours().toString().padStart(2, '0');
  const min = date.getMinutes().toString().padStart(2, '0');
  const s = date.getSeconds().toString().padStart(2, '0');
  return `${d} ${m} ${y}, ${h}:${min}:${s}`;
}

export const knownStreamUrls = [
  { label: 'Radio Nueva Actitud (actual)', url: 'https://stream.radioactitud.com/live' },
  { label: 'Los 40 Principales', url: 'https://stream.los40.com/live.mp3' },
  { label: 'Radio Mitre', url: 'https://stream.radiomitre.com/live' },
  { label: 'Rock & Pop', url: 'https://stream.rockandpop.com/live.aac' },
];